import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const DEMO_PASSWORD = "demo1234";

const DEMO_ACCOUNTS = [
  {
    email: "admin@demo.ezyclass",
    role: "admin" as const,
    display_name: "Quản trị viên Demo",
    avatar_key: "cat",
  },
  {
    email: "teacher@demo.ezyclass",
    role: "teacher" as const,
    display_name: "Cô Lê Phương Anh",
    avatar_key: "star",
  },
  {
    email: "student@demo.ezyclass",
    role: "student" as const,
    display_name: "Trần Hoàng An",
    avatar_key: "rocket",
  },
];

/**
 * Đảm bảo 3 tài khoản demo tồn tại (admin/teacher/student). Idempotent.
 *
 * Lưu ý bảo mật: endpoint này KHÔNG yêu cầu auth vì nó được gọi từ trang
 * landing trước khi user đăng nhập (luồng "Dùng thử demo"). Vì vậy:
 *   - Chỉ thao tác trên 3 email demo hardcoded (allowlist).
 *   - KHÔNG dùng listUsers (tránh liệt kê toàn bộ user của project).
 *   - KHÔNG reset password nếu account đã tồn tại (tránh biến endpoint
 *     thành công cụ chiếm tài khoản).
 *   - Short-circuit nếu cả 3 profile demo đã tồn tại — không tốn auth API call.
 */
export const ensureDemoAccounts = createServerFn({ method: "POST" }).handler(
  async () => {
    // Fast path: nếu cả 3 profile demo đã tồn tại trong bảng profiles → no-op.
    const demoEmails = DEMO_ACCOUNTS.map((a) => a.email);
    const { data: existingProfiles } = await supabaseAdmin
      .from("profiles")
      .select("id, email")
      .in("email", demoEmails);
    const profileByEmail = new Map(
      (existingProfiles ?? []).map((p) => [p.email, p.id as string]),
    );
    if (profileByEmail.size === DEMO_ACCOUNTS.length) {
      return { ok: true };
    }

    for (const acc of DEMO_ACCOUNTS) {
      let userId = profileByEmail.get(acc.email);

      if (!userId) {
        // Tạo auth user. Nếu đã tồn tại (race / account có nhưng thiếu profile)
        // createUser sẽ lỗi "User already registered" — bỏ qua và tiếp tục.
        const { data: created, error: createErr } =
          await supabaseAdmin.auth.admin.createUser({
            email: acc.email,
            password: DEMO_PASSWORD,
            email_confirm: true,
            user_metadata: { demo: true },
          });
        if (createErr) {
          // Có thể là "already registered" — không có cách lookup theo email
          // qua admin API; log và bỏ qua account này lần này.
          console.error(`[demo] create ${acc.email}:`, createErr.message);
          continue;
        }
        userId = created.user?.id;
      }

      if (!userId) continue;

      await supabaseAdmin.from("profiles").upsert(
        {
          id: userId,
          email: acc.email,
          display_name: acc.display_name,
          avatar_key: acc.avatar_key,
          onboarded: true,
        },
        { onConflict: "id" },
      );

      const { data: existingRoles } = await supabaseAdmin
        .from("user_roles")
        .select("role")
        .eq("user_id", userId);
      const hasRole = (existingRoles ?? []).some((r) => r.role === acc.role);
      if (!hasRole) {
        await supabaseAdmin
          .from("user_roles")
          .insert({ user_id: userId, role: acc.role });
      }
    }

    return { ok: true };
  },
);
